import {
  Box,
  Card,
  CardContent,
  Typography,
  type SxProps,
  type Theme
} from "@mui/material";

import { DARK_MODE_IMAGE_FILTER, isDarkMode } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";
import { homePageHeroColors } from "@/src/theme/tokens";

import { HomePageButton } from "@/src/pages/consumer/HomePage/HomePageContent";

const cardLabelSx: SxProps<Theme> = schemeSx((muiTheme) =>
  isDarkMode(muiTheme) ? { color: muiTheme.palette.text.primary } : {}
);

const cardButtonTextSx: SxProps<Theme> = schemeSx((muiTheme) =>
  isDarkMode(muiTheme) ? { color: muiTheme.palette.text.primary } : {}
);

const cardSx: SxProps<Theme> = schemeSx((muiTheme) => ({
  display: "flex",
  position: "relative",
  minWidth: ["100%", "100%", "280px"],
  maxWidth: ["100%", "340px"],
  width: "100%",
  height: "158px",
  borderRadius: "40px",
  p: 2,
  boxSizing: "border-box",
  ...(isDarkMode(muiTheme) && {
    backgroundColor: homePageHeroColors.auctionCardDarkBackground,
    border: "none",
    boxShadow: "none",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "inherit",
      border: `1px solid ${muiTheme.palette.semantic.primary.focus}`,
      pointerEvents: "none",
      zIndex: 1
    }
  })
}));

const buildVehicleImageSx = (
  backgroundUrl: string[],
  width: number,
  height: number,
  imageRightOffset: number,
  imageTopOffset?: number
): SxProps<Theme> =>
  schemeSx((muiTheme) => ({
    backgroundImage: backgroundUrl,
    position: "absolute",
    backgroundRepeat: "no-repeat",
    backgroundSize: "contain",
    backgroundPosition: "right bottom",
    width: `${width}px`,
    height: `${height}px`,
    right: imageRightOffset,
    ...(imageTopOffset !== undefined && { top: imageTopOffset }),
    ...(isDarkMode(muiTheme) && {
      filter: DARK_MODE_IMAGE_FILTER
    })
  }));

export type VehicleImageSize = "small" | "medium" | "large";

const VEHICLE_IMAGE_SIZE_OPTIONS: Record<
  VehicleImageSize,
  { width: number; height: number }
> = {
  small: { width: 180, height: 112 },
  medium: { width: 200, height: 125 },
  large: { width: 200, height: 125 }
};

interface VehicleStartAuctionCardContentProps {
  label: string;
  backgroundUrl: string[];
  onAuctionListingClick: () => void;
  imageSize?: VehicleImageSize;
  imageRightOffset?: number;
  imageTopOffset?: number;
  abTestId?: string;
}

const VehicleStartAuctionCard = ({
  label,
  backgroundUrl,
  onAuctionListingClick,
  imageSize = "medium",
  imageRightOffset = -30,
  imageTopOffset,
  abTestId
}: VehicleStartAuctionCardContentProps) => {
  const { width, height } = VEHICLE_IMAGE_SIZE_OPTIONS[imageSize];

  return (
    <Card sx={cardSx}>
      <Box
        sx={buildVehicleImageSx(
          backgroundUrl,
          width,
          height,
          imageRightOffset,
          imageTopOffset
        )}
      />
      <Box sx={{ position: "relative", pb: 6, zIndex: 2 }}>
        <CardContent sx={{ p: 1, maxWidth: "60%" }}>
          <Typography component="div" variant="h5" sx={cardLabelSx}>
            {label}
          </Typography>
        </CardContent>
        <HomePageButton
          color="primary"
          variant="contained"
          onClick={onAuctionListingClick}
          buttonText="Aloita nyt"
          abTestId={abTestId}
          textSx={cardButtonTextSx}
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "max-content"
          }}
        />
      </Box>
    </Card>
  );
};

export default VehicleStartAuctionCard;
